import React, { useState, useEffect } from "react";
import { useContext } from "react";
import { DataContext } from "../DataContext";
import { useAuth0 } from "@auth0/auth0-react";
import { deleteCart } from "../api/cart";
import { createOrder } from "../api/order";
import CheckoutProduct from "../components/CheckoutProduct";
import { PageTemplate, HeaderTemplate } from "../styles";
import styled from "styled-components";
import toast from "react-hot-toast";

const Card = styled.div`
  margin-bottom: 20px;
  &.products {
    margin-bottom: 0;
    border-radius: 5px;
    color: var(--dark-grey);
    overflow: hidden;
    height: 100%;
    background-color: var(--off-white);
    .success:last-child {
      border-bottom: 0;
      box-shadow: 0;
    }
    .success-products {
      border-bottom: 1px solid var(--border);
      background-color: var(--white);
    }
    .product-review {
      padding: 30px;
      background-color: var(--off-white);
      .total {
        display: flex;
        justify-content: space-between;
        align-items: center;
        span {
          font-weight: 700;
          &.value {
            text-align: right;
          }
        }
        &.shipping,
        &.salestax {
          margin-bottom: 10px;
        }
      }
      @media only screen and (max-width: 991px) {
        padding: 20px;
      }
    }
  }
  .card-info {
    margin-top: 10px;
    font-weight: 700;
    font-style: italic;
  }
  h2 {
    span {
      margin-left: 20px;
    }
    @media only screen and (max-width: 767px) {
      font-size: 18px;
    }
  }
`;

function Success() {
  const { user, isAuthenticated, isLoading, getAccessTokenSilently } = useAuth0();
  const { productsInCart, clearCartState, getCustomer } = useContext(DataContext);
  const [cartData, setCartData] = useState(null);
  const [order, setOrder] = useState(null);

  // get query string parameter from url
  const urlParams = new URLSearchParams(window.location.search);
  const paymentIntentId = urlParams.get("payment_intent");

  // component mount
  useEffect(() => {
    setCartData(productsInCart);
  }, []);

  useEffect(() => {
    if (cartData) {
      const fetchOrder = async () => {
        try {
          const response = await createOrder(user?.sub, cartData, paymentIntentId);
          setOrder(response);
          clearCartState();
          if (isAuthenticated && !isLoading) {
            const token = await getAccessTokenSilently();
            const headers = {
              "Content-Type": "application/json",
              authorization: `Bearer ${token}`,
            };
            deleteCart(user.sub, headers);
            getCustomer();
          }
        } catch (error) {
          console.error(error);
          toast.error(error.message);
        }
      };
      fetchOrder();
    }
  }, [cartData]);

  if (!order) return null;

  const shippingCosts = order.shippingRate;
  const salesTax = order.salesTax;

  return (
    <>
      <HeaderTemplate className="narrow">
        <div className="header">
          <div className="title">
            <h1>Thank You For Your Purchase!</h1>
          </div>
        </div>
      </HeaderTemplate>
      <PageTemplate className="narrow">
        <div className="body columns success">
          <div className="left-column">
            <Card className="products">
              <div className="success-products">
                {order.products.map((currentProduct, idx) => (
                  <CheckoutProduct success={true} key={idx} product={currentProduct}></CheckoutProduct>
                ))}
              </div>

              <div className="product-review">
                <div className="total shipping">
                  <span>Shipping</span>
                  <span className="value">${shippingCosts.toFixed(2)}</span>
                </div>
                {salesTax > 0 && (
                  <div className="total salestax">
                    <span>Sales Tax</span>
                    <span className="value">${salesTax.toFixed(2)}</span>
                  </div>
                )}
                <div className="total">
                  <span>Total</span>
                  <span className="value">${order.total.toFixed(2)}</span>
                </div>
              </div>
            </Card>
          </div>
          <div className="right-column">
            <Card>
              <h2>Order # {order.orderNumber}</h2>
              <div>{order.billing.email}</div>
            </Card>

            <Card>
              <h2>Shipping</h2>
              <div>{order.shipping.name}</div>
              <div>{order.shipping.address.line1}</div>
              <div>{order.shipping.address.line2}</div>
              <div>
                {order.shipping.address.city} {order.shipping.address.state} {order.shipping.address.postal_code}
              </div>
            </Card>

            <Card>
              <h2>Billing</h2>
              <div>{order.billing.name}</div>
              <div>{order.billing.address.line1}</div>
              <div>{order.billing.address.line2}</div>
              <div>
                {order.billing.address.city} {order.billing.address.state} {order.billing.address.postal_code}
              </div>
              <div className="card-info">
                {order.billing.card.brand} ({order.billing.card.last4})
              </div>
            </Card>
          </div>
        </div>
      </PageTemplate>
    </>
  );
}

export default Success;
